import AddIcon from '@mui/icons-material/Add';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReplayIcon from '@mui/icons-material/Replay';
import { Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import CloakFetcher from 'cloak-auth/src/Client/CloakFetcher';
import { useAuthStore } from 'cloak-auth/src/Client/stores';
import { useClaimTag, useGetInstances, useGetRenkiTag, useHasRedirectPermissions } from 'gen/client';
import { TagClaimTagDTO } from 'gen/model';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR from 'swr';

import GetFormInput from '@ui/Components/Form/GetFormInput';
import CustomDialog from '@ui/Components/Modals/CustomDialog';

import { Company, SocialGroupList } from 'kerberus-components/src/Types/AuthServer';
import { claimTagSuccess, customMessageErrorToast, customSuccessToast } from 'kerberus-components/src/Utils/toasts';

interface ClaimTagError {
    info: {
        message: string;
    };
}
interface NoOwnerViewProps {
    onScanAgain: () => void;
    serial: string;
}

export default function NoOwnerView({ onScanAgain, serial }: Readonly<NoOwnerViewProps>) {
    const [open, setOpen] = useState<boolean>(false);
    const userId = useAuthStore.getState().subject;

    // Claim tag hooks
    const { trigger: claimTag } = useClaimTag();
    const { data: instances = [] } = useGetInstances({
        swr: {
            refreshInterval: 0,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
        },
    });
    const { data: tagData, mutate: mutateTag } = useGetRenkiTag(serial, {
        swr: {
            enabled: !!serial,
            refreshInterval: 0,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
        },
    });

    // Owner permission swr (to update isOwner view state)
    const { mutate: mutateOwnerPerm } = useHasRedirectPermissions(serial, {
        swr: {
            enabled: !!serial,
            refreshInterval: 0,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
        },
    });

    // Form for claiming owner
    const { control, formState, handleSubmit, reset, setValue, watch } = useForm<TagClaimTagDTO>({
        defaultValues: {
            customer_company: '',
            customer_group: '',
            customer_instance: '',
            customer_user: '',
            instance: '',
            serial: serial,
        },
    });
    const { isSubmitting } = formState;

    // If tag already linked to instance fill it
    useEffect(() => {
        if (tagData?.tag?.instance) {
            setValue('instance', tagData.tag.instance);
        }
    }, [tagData?.tag?.instance, setValue]);

    const [selectedType, setSelectedType] = useState<string>('');
    const handleSelectTypeChange = (e: { target: { value: string } }) => {
        const nextType = e.target.value;
        setSelectedType(nextType);
        reset((prev) => ({
            ...prev,
            customer_company: '',
            customer_group: '',
            customer_instance: '',
            customer_user: '',
        }));
        if (nextType === 'user') {
            setValue('customer_user', userId);
        }
    };

    const instance = watch('instance');
    const customerCompany = watch('customer_company');
    const customerGroup = watch('customer_group');
    const customerInstance = watch('customer_instance');
    const customerUser = watch('customer_user');

    const selectedInstance = useMemo(() => instances.find((i) => i.cloak_role === instance), [instances, instance]);

    const { data: companies = [] } = useSWR<Company[]>(
        selectedType === 'company' && selectedInstance?.domain ? ['users/company/', selectedInstance.id] : null,
        (route: string) => CloakFetcher(route, selectedInstance?.domain),
    );
    const { data: groups = [] } = useSWR<SocialGroupList[]>(
        selectedType === 'group' && selectedInstance?.domain ? ['users/socialgrouplist/', selectedInstance.id] : null,
        (route: string) => CloakFetcher(route, selectedInstance?.domain),
    );

    const isSubmitDisabled = !customerCompany && !customerGroup && !customerInstance && !customerUser;

    const onSubmit = async (form: TagClaimTagDTO) => {
        try {
            await claimTag(form);
            customSuccessToast(claimTagSuccess);
            // Revalidate tag data and permission so flow hook picks up owner changes
            if (typeof mutateTag === 'function') {
                void mutateTag();
            }
            if (typeof mutateOwnerPerm === 'function') {
                void mutateOwnerPerm();
            }
            // Close dialog; parent component will rerender and useTagScanFlow will compute new state
            setOpen(false);
        } catch (error) {
            const typedError = error as ClaimTagError;
            customMessageErrorToast(typedError.info.message);
            console.error('Error adding tag redirect:', typedError.info.message);
        }
    };

    return (
        <>
            <Stack
                direction="column"
                spacing={2}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 4,
                    textAlign: 'center',
                }}
            >
                <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 56 }} />
                <Typography
                    sx={{
                        color: 'text.secondary',
                    }}
                    variant="body2"
                >
                    This tag is valid but has no owner. You can set an owner for this tag. After setting owner you can
                    manage the tag.
                </Typography>
                <Stack spacing={2}>
                    <Button
                        color="primary"
                        onClick={() => {
                            setOpen(true);
                        }}
                        size="large"
                        startIcon={<AddIcon fontSize="inherit" />}
                        variant="contained"
                    >
                        Set Owner
                    </Button>
                    <Button
                        color="inherit"
                        onClick={onScanAgain}
                        size="large"
                        startIcon={<ReplayIcon fontSize="inherit" />}
                        variant="outlined"
                    >
                        Scan Again
                    </Button>
                </Stack>
            </Stack>
            <CustomDialog
                content={{
                    body: (
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
                        >
                            <Stack spacing={2} sx={{ mt: 1, width: { sm: 360, xs: '100%' } }}>
                                <Typography
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                    variant="h6"
                                >
                                    Claim Tag
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'text.secondary',
                                        textAlign: 'center',
                                    }}
                                    variant="body2"
                                >
                                    Fill in the form below to claim the tag. The tag will be linked to the selected
                                    instance.
                                </Typography>
                                {!tagData?.tag?.instance && (
                                    <GetFormInput
                                        control={control}
                                        fullWidth
                                        helperText="Select the instance to which the tag should be linked."
                                        label="Instances"
                                        options={instances.map((inst) => ({
                                            label: inst.name,
                                            value: inst.cloak_role,
                                        }))}
                                        required
                                        type="selectOne"
                                        value="instance"
                                    />
                                )}
                                {instance ? (
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Tag Owner</InputLabel>
                                        <Select
                                            label="Tag Owner"
                                            onChange={handleSelectTypeChange}
                                            value={selectedType}
                                        >
                                            <MenuItem value="user">For me</MenuItem>
                                            <MenuItem value="company">Company</MenuItem>
                                            <MenuItem value="group">Group</MenuItem>
                                            <MenuItem value="instance">Instance Operator</MenuItem>
                                        </Select>
                                        <FormHelperText>Owner type of the tag</FormHelperText>
                                    </FormControl>
                                ) : null}
                                {selectedType === 'instance' && (
                                    <GetFormInput
                                        control={control}
                                        fullWidth
                                        helperText="Instance operator with tag access."
                                        label="Instances"
                                        options={instances.map((inst) => ({
                                            label: inst.name,
                                            value: inst.cloak_role,
                                        }))}
                                        type="selectOne"
                                        value="customer_instance"
                                    />
                                )}
                                {selectedType === 'company' && (
                                    <GetFormInput
                                        control={control}
                                        fullWidth
                                        helperText="Company with tag access."
                                        label="Company"
                                        options={companies.map((c) => ({ label: c.name, value: c.id }))}
                                        type="selectOne"
                                        value="customer_company"
                                    />
                                )}
                                {selectedType === 'group' && (
                                    <GetFormInput
                                        control={control}
                                        fullWidth
                                        helperText="Group with tag access."
                                        label="Group"
                                        options={groups.map((g) => ({ label: g.name, value: g.id }))}
                                        type="selectOne"
                                        value="customer_group"
                                    />
                                )}
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        mt: 2,
                                    }}
                                >
                                    <Button
                                        onClick={() => setOpen(false)}
                                        sx={{ alignSelf: 'flex-end' }}
                                        variant="outlined"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        disabled={isSubmitDisabled || isSubmitting}
                                        sx={{ alignSelf: 'flex-end', bgcolor: '#1C4E71' }}
                                        type="submit"
                                        variant="contained"
                                    >
                                        {isSubmitting ? 'Applying…' : 'Apply'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    ),
                    title: 'Claim Tag',
                }}
                hideActions
                onClose={() => setOpen(false)}
                open={open}
            />
        </>
    );
}
